import { Trans, useLingui } from "@lingui/react/macro";
import type { KnowledgeConnectionView } from "@rakazo/contracts";
import { KNOWLEDGE_FILE_EXTENSIONS } from "@rakazo/contracts";
import { Button, Disclosure, Input, Spinner, Textarea } from "@rakazo/ui-web";
import { SwitchField } from "@rakazo/ui-web/components/ui/field";
import { useId, useState } from "react";

export interface KnowledgeConnectionValues {
  provider: "weknora";
  baseUrl: string;
  apiKey?: string;
  embeddingModelId?: string;
  summaryModelId?: string;
  visionModelId?: string;
  rerankModelId?: string;
  enabled: boolean;
}

export function KnowledgeConnectionForm({
  connection,
  saving,
  error,
  onSave,
  onCancel,
}: {
  connection: KnowledgeConnectionView | null;
  saving: boolean;
  error: string | null;
  onSave: (values: KnowledgeConnectionValues) => void;
  onCancel?: () => void;
}) {
  const { t } = useLingui();
  const id = useId();
  const [baseUrl, setBaseUrl] = useState(connection?.baseUrl ?? "");
  const [apiKey, setApiKey] = useState("");
  const [embeddingModelId, setEmbeddingModelId] = useState(connection?.embeddingModelId ?? "");
  const [summaryModelId, setSummaryModelId] = useState(connection?.summaryModelId ?? "");
  const [visionModelId, setVisionModelId] = useState(connection?.visionModelId ?? "");
  const [rerankModelId, setRerankModelId] = useState(connection?.rerankModelId ?? "");
  const [enabled, setEnabled] = useState(connection?.enabled ?? true);
  return (
    <form
      aria-label={t`Knowledge connection`}
      className="space-y-4 rounded-xl border border-border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (saving) return;
        onSave({
          provider: "weknora",
          baseUrl: baseUrl.trim(),
          ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
          embeddingModelId: embeddingModelId.trim(),
          summaryModelId: summaryModelId.trim(),
          visionModelId: visionModelId.trim(),
          rerankModelId: rerankModelId.trim(),
          enabled,
        });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-url`} className="text-sm font-medium">
            <Trans>Service URL</Trans>
          </label>
          <Input
            id={`${id}-url`}
            type="url"
            required
            placeholder="https://knowledge.example.com"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.target.value)}
            disabled={saving}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-key`} className="text-sm font-medium">
            <Trans>API key</Trans>
          </label>
          <Input
            id={`${id}-key`}
            type="password"
            autoComplete="new-password"
            required={!connection?.apiKeySet}
            placeholder={connection?.apiKeySet ? t`Set (leave blank to keep)` : ""}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            disabled={saving}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-embedding`} className="text-sm font-medium">
          <Trans>Embedding model ID</Trans>
        </label>
        <Input
          id={`${id}-embedding`}
          required
          value={embeddingModelId}
          onChange={(event) => setEmbeddingModelId(event.target.value)}
          disabled={saving}
        />
      </div>
      <Disclosure
        summary={<Trans>Advanced</Trans>}
        summaryClassName="text-sm"
        contentClassName="pt-3"
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor={`${id}-summary`} className="text-sm font-medium">
              <Trans>Summary model ID (optional)</Trans>
            </label>
            <Input
              id={`${id}-summary`}
              value={summaryModelId}
              onChange={(event) => setSummaryModelId(event.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={`${id}-rerank`} className="text-sm font-medium">
              <Trans>Rerank model ID (optional)</Trans>
            </label>
            <Input
              id={`${id}-rerank`}
              value={rerankModelId}
              onChange={(event) => setRerankModelId(event.target.value)}
              disabled={saving}
            />
          </div>
          <SwitchField
            label={<Trans>Enabled</Trans>}
            checked={enabled}
            onCheckedChange={setEnabled}
            disabled={saving}
          />
          <div className="space-y-1 sm:col-span-2">
            <label htmlFor={`${id}-vision`} className="text-sm font-medium">
              <Trans>Vision model ID (optional)</Trans>
            </label>
            <Input
              id={`${id}-vision`}
              value={visionModelId}
              onChange={(event) => setVisionModelId(event.target.value)}
              disabled={saving}
            />
          </div>
        </div>
      </Disclosure>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? <Spinner aria-hidden="true" /> : null}
          <Trans>Save connection</Trans>
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" disabled={saving} onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
        ) : null}
      </div>
    </form>
  );
}

export function KnowledgeCollectionForm({
  saving,
  error,
  onSave,
  onCancel,
}: {
  saving: boolean;
  error: string | null;
  onSave: (values: { name: string; description?: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useLingui();
  const id = useId();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  return (
    <form
      aria-label={t`New collection`}
      className="space-y-3 rounded-xl border border-border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!saving && name.trim())
          onSave({
            name: name.trim(),
            ...(description.trim() ? { description: description.trim() } : {}),
          });
      }}
    >
      <div className="space-y-1">
        <label htmlFor={`${id}-name`} className="text-sm font-medium">
          <Trans>Name</Trans>
        </label>
        <Input
          id={`${id}-name`}
          required
          maxLength={120}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={saving}
        />
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-description`} className="text-sm font-medium">
          <Trans>Description (optional)</Trans>
        </label>
        <Textarea
          id={`${id}-description`}
          maxLength={1000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          disabled={saving}
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={saving || !name.trim()}>
          {saving ? <Spinner aria-hidden="true" /> : null}
          <Trans>Create collection</Trans>
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </form>
  );
}

export function KnowledgeUploadForm({
  uploading,
  maxUploadSizeBytes,
  replaces,
  error,
  onUpload,
  onCancel,
}: {
  uploading: boolean;
  maxUploadSizeBytes: number;
  replaces?: { title: string; version: string } | null;
  error: string | null;
  onUpload: (file: File, metadata: { title?: string; version: string }) => void;
  onCancel: () => void;
}) {
  const { t } = useLingui();
  const id = useId();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState(replaces?.title ?? "");
  const [version, setVersion] = useState(replaces ? "" : "1");
  const [problem, setProblem] = useState<string | null>(null);
  const maxMiB = Math.floor(maxUploadSizeBytes / 1024 / 1024);
  return (
    <form
      aria-label={t`Upload document`}
      className="space-y-3 rounded-xl border border-border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (!file || uploading || !version.trim()) return;
        onUpload(file, {
          version: version.trim(),
          ...(title.trim() ? { title: title.trim() } : {}),
        });
      }}
    >
      {replaces ? (
        <p className="text-sm text-muted-foreground">
          <Trans>
            Replaces {replaces.title}, version {replaces.version}, when published.
          </Trans>
        </p>
      ) : null}
      <div className="space-y-1">
        <label htmlFor={`${id}-file`} className="text-sm font-medium">
          <Trans>File</Trans>
        </label>
        <Input
          id={`${id}-file`}
          type="file"
          accept={KNOWLEDGE_FILE_EXTENSIONS.join(",")}
          required
          disabled={uploading}
          onChange={(event) => {
            const next = event.target.files?.[0] ?? null;
            if (
              next &&
              !KNOWLEDGE_FILE_EXTENSIONS.some((extension) =>
                next.name.toLowerCase().endsWith(extension),
              )
            ) {
              setProblem(t`Choose a PDF, DOCX, TXT or Markdown file.`);
              setFile(null);
            } else if (next && (next.size === 0 || next.size > maxUploadSizeBytes)) {
              setProblem(t`Choose a non-empty file up to ${maxMiB} MiB.`);
              setFile(null);
            } else {
              setProblem(null);
              setFile(next);
            }
          }}
        />
        <p className="text-xs text-muted-foreground">
          <Trans>PDF, DOCX, TXT or Markdown · Up to {maxMiB} MiB</Trans>
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,8rem)]">
        <div className="space-y-1">
          <label htmlFor={`${id}-title`} className="text-sm font-medium">
            <Trans>Title (optional)</Trans>
          </label>
          <Input
            id={`${id}-title`}
            maxLength={200}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={uploading}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-version`} className="text-sm font-medium">
            <Trans>Version</Trans>
          </label>
          <Input
            id={`${id}-version`}
            required
            maxLength={80}
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            disabled={uploading}
          />
        </div>
      </div>
      {problem || error ? (
        <p role="alert" className="text-sm text-destructive">
          {problem ?? error}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={uploading || !file || !version.trim()}>
          {uploading ? <Spinner aria-hidden="true" /> : null}
          {uploading ? <Trans>Uploading…</Trans> : <Trans>Upload</Trans>}
        </Button>
        <Button type="button" variant="ghost" disabled={uploading} onClick={onCancel}>
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </form>
  );
}
